import { useState, useEffect, Fragment } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  ListItem,
  OrderedList,
  Grid,
  GridItem,
  Card,
  CardBody,
  Spinner,
} from "@chakra-ui/react";
import { Tabs, TabList, Tab, TabPanels, TabPanel } from "components/FastTabs";
import { useQuery, useQueryClient } from "react-query";
import { getMetricOptions } from "components/MetricInput";
import IssbContainer from "./IssbContainer";
import PageHeader from "components/text/PageHeader";
import templatePackageService from "api/issb/templatepackage.service";
import templateQuestionService from "api/issb/templatequestion.service";
import { useAuth } from "context";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { routes } from "config/paths";
import { AButton } from "components/A";
import { ArrowCounterClockwise, ArrowLeft, CaretDown, CaretUp, Trash } from "@phosphor-icons/react";
import PropInput from "components/PropInput";
import { H3, H4 } from "components/text/headings";
import { SmallText } from "components/text/TextTypes";
import HighMediumLow from "api/common/HighMediumLow";
import LifecycleState from "api/common/LifecycleState";
import QuestionPartsList from "./IssbDataInput/QuestionPartsList";
import QAPart, { QAPartType } from "./IssbDataInput/QAPart";
import { QuestionHeader, QuestionSpec } from "./IssbDataInput/AnswerDetailPage";
import MarkdownText from "components/MarkdownText";
import A from "components/A";
import { formatDate, showErrorToast } from "utils";
import { toArray } from "utils/text-utils";
import CommentSection from "pages/ISSB/IssbDataInput/components/CommentSection";
import { useToast } from "@chakra-ui/react";
import assert from "utils/assert";
import { useRerender } from "rerenderer";
import { OPTIONS_COUNTRIES_MARKER, OPTIONS_RIVERS_MARKER } from "./IssbDataInput/QuestionPartEditor";
import { useSelector } from "react-redux";
import questionnaireService from "api/issb/questionnaire.service";

function IssbTemplateQuestionEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [packageId, setPackageId] = useState(null);
  const [framework, setFramework] = useState(null);
  const { getAccessTokenSilently } = useAuth();
  const [templateQuestion, setTemplateQuestion] = useState({});
  //   console.log("IssbTemplateQuestionEditorPage", templateQuestion);
  const queryClient = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    if (!id) {
      return;
    }
    getAccessTokenSilently().then((token) => {
      templateQuestionService.get(id).then((tq) => {
        setTemplateQuestion(tq);
        if (!tq.packageId) {
          console.error("No packageId for template question", tq);
          showErrorToast(toast, "No packageId for template question " + tq.id);
          return;
        }
        setPackageId(tq.packageId);
        templatePackageService.get(tq.packageId).then(setFramework);
      });
    });
  }, [id, getAccessTokenSilently, toast]);

  const {
    data: templateQuestionsPage,
    isLoading,
    error,
  } = useQuery(
    ["template-questions", packageId],
    () => {
      return getAccessTokenSilently().then((token) => {
        return templateQuestionService.list({ query: packageId ? "packageId:" + packageId : null, size: 10000 }); // 10k - no need to paginate
      });
    },
    {
      enabled: !!getAccessTokenSilently && !!packageId,
    }
  );

  const [isSaving, setIsSaving] = useState(false);
  const handleSave = () => {
    setIsSaving(true);
    let p;
    if (templateQuestion.id) {
      p = templateQuestionService.update(templateQuestion.id, templateQuestion);
    } else {
      p = templateQuestionService.create(templateQuestion);
    }
    p.then((pkg) => {
      console.log("saved pkg", pkg);
      // copy over server-side updates
      templateQuestion.conditionReferences = pkg.conditionReferences;
      templateQuestion.conditionReferencesDownstream = pkg.conditionReferencesDownstream;
      templateQuestion.lastModifiedDate = pkg.lastModifiedDate;
      queryClient.invalidateQueries(["template-questions", templateQuestion.packageId]);
      refresh();
    }).finally(() => {
      setIsSaving(false);
    });
  };

  const handleDiscard = () => {
    navigate(-1);
  };

  const handleApprove = () => {
    templateQuestion.state = LifecycleState.ACTIVE;
    handleSave();
  };

  const handleMarkAsDraft = () => {
    templateQuestion.state = LifecycleState.DRAFT;
    handleSave();
  };

  const handleArchive = () => {
    templateQuestion.state = LifecycleState.ARCHIVED;
    templateQuestionService.delete(templateQuestion.id).then(() => {
      queryClient.invalidateQueries(["template-questions", templateQuestion.packageId]);
      navigate(-1);
    });
  };

  const handleUpdateExistingRoundQuestions = () => {
    setIsSaving(true);
    console.log("TODO handleUpdateExistingRoundQuestions", templateQuestion);
    templateQuestionService
      .updateExistingRounds({ templateQuestionId: templateQuestion.id })
      .then((r) => {
        console.log("updateExistingRounds DONE", r);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const roundSelector = useSelector((state) => state.issbRound);
  const currentRoundId = roundSelector?.currentRoundId;
  const {data: currentRound} = useQuery(currentRoundId, async () => {
    await getAccessTokenSilently();
    return questionnaireService.get(currentRoundId);
  }, {
    enabled: !!currentRoundId
  });
  const handleAddToRound = () => {
    setIsSaving(true);
    questionnaireService.doAddQuestionFromTemplateQuestion(currentRoundId, templateQuestion.id).finally(() => {
      setIsSaving(false);
    });
  };

  // a way to nudge a re-render
  const { rerender: refresh } = useRerender();

  const [questionParts, _setQuestionParts] = useState(templateQuestion?.parts || []);
  useEffect(() => {
    _setQuestionParts(templateQuestion?.parts || []);
  }, [templateQuestion?.parts]);

  const setQuestionParts = (parts) => {
    _setQuestionParts(parts);
    templateQuestion.parts = parts;
    refresh();
  };

  let allQRefs = templateQuestionsPage?.content?.filter((q) => q.issbReference).map((q) => q.issbReference) || [];
  allQRefs = [...new Set(allQRefs)];
  allQRefs.sort();

  const issues = [];
  // rows?
  const rowParts = questionParts.filter((p) => p.type === QAPartType.table);
  if (rowParts.length > 1) {
    issues.push("There can be at most one `rows` setting");
  }

  return (
    <IssbContainer spacing={4} margin="1%">
      <PageHeader superTitle={framework?.name} title="Template Question Editor" />
      {packageId && (
        <A href={`/app/issb/template-question?packageId=${packageId}`} color="limeShade.600">
          Back to question list
        </A>
      )}
      <Grid templateColumns="repeat(2, 1fr)" gap={6} p={4}>
        <GridItem>
          <Card>
            <CardBody>
              <PropInput
                label="Clause or Question Number (reference)"
                prop="issbReference"
                item={templateQuestion}
                help="Clause in the regulation or question-number in CDP"
              />
              <PropInput prop="question" item={templateQuestion} type="textarea" rows={2} />

              <H3>Question Parts?</H3>
              <SmallText>
                Question parts are optional. They replace the big question with a list of questions -- which can have
                different types and custom editors.
              </SmallText>
              <OrderedList styleType="lower-alpha">
                {questionParts.map((part, index) => (
                  <QuestionPartItemEditor
                    key={index}
                    part={part}
                    index={index}
                    handleDeletePart={() => setQuestionParts(questionParts.filter((_, i) => i !== index))}
                    refresh={refresh}
                  />
                ))}
              </OrderedList>

              <Button variant="secondary" size="sm" onClick={() => setQuestionParts([...questionParts, {}])}>
                Add Part
              </Button>
              <PropInput prop="questionGroup" item={templateQuestion} />
              <hr style={{ margin: "1rem" }} />
              <PropInput prop="description" item={templateQuestion} type="textarea" rows={2} />
              <PropInput prop="controlDescription" item={templateQuestion} type="textarea" rows={2} />
              <PropInput prop="controlObjective" item={templateQuestion} type="textarea" rows={2} />
              <PropInput prop="evidenceOfControl" item={templateQuestion} type="textarea" rows={2} />

              <hr style={{ margin: "1rem" }} />
              <PropInput
                prop="regulation"
                item={templateQuestion}
                help="The regulation e.g. 'IFRS S1' or e.g. 'CDP Module 7'"
              />
              <PropInput
                label="Depends-On References (calculated on save)"
                prop="conditionReferences"
                readOnly
                item={templateQuestion}
                type="select"
                multiple={true}
                options={allQRefs}
                help="References used in the condition e.g. if this question depends on an earlier question."
              />
              <Text>
                Questions that depend on this one: {templateQuestion.conditionReferencesDownstream?.join(", ")}
              </Text>
              <PropInput
                label="Related / Similar Question References"
                prop="linkedReferences"
                item={templateQuestion}
                type="select"
                multiple={true}
                options={allQRefs}
                help="Other questions that we link to this one"
              />
              <PropInput
                prop="condition"
                item={templateQuestion}
                type="text"
                help='Optional. Only show this part if the condition is true. E.g. `answer("1.8") = "Apples" OR answer("1.8") = "Oranges"` Comparisons are case-insensitive (so "apples" = "Apples"). Substring matching: `contains`, e.g. `"chocolate" contains "choc"`'
              />

              <hr style={{ margin: "1rem" }} />
              <PropInput prop="sectors" item={templateQuestion} />
              <PropInput prop="tags" item={templateQuestion} />
              <PropInput prop="roleLabel" item={templateQuestion} />
              <hr style={{ margin: "1rem" }} />
              <PropInput
                label="Optional"
                prop="optional"
                item={templateQuestion}
                type="checkbox"
                help="If true, this question is optional for clients to answer"
              />
              <PropInput
                prop="sensitivity"
                item={templateQuestion}
                type="select"
                multiple={false}
                options={[HighMediumLow.HIGH, HighMediumLow.MEDIUM, HighMediumLow.LOW]}
              />
              <PropInput
                prop="decisionUsefulness"
                item={templateQuestion}
                type="select"
                multiple={false}
                options={[HighMediumLow.HIGH, HighMediumLow.MEDIUM, HighMediumLow.LOW]}
              />

              <Box mt={4}>
                {issues.map((issue) => (
                  <Text key={issue} color="red">
                    {issue}
                  </Text>
                ))}
              </Box>
              <Flex mt={4} gap={2}>
                <Button variant="secondary" onClick={handleDiscard}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} isDisabled={isSaving}>
                  Save{isSaving && <Spinner size="sm" />}
                </Button>
                <Text>
                  State:{" "}
                  {templateQuestion.state
                    ? { ACTIVE: "Ready for use", DRAFT: "Draft", ARCHIVED: "Archived" }[templateQuestion.state]
                    : "New"}
                  <br />
                  {formatDate(templateQuestion.lastModifiedDate)}
                </Text>
                {(!templateQuestion.state || templateQuestion.state === LifecycleState.DRAFT) && (
                  <Button variant="secondary" onClick={handleApprove}>
                    Approve
                  </Button>
                )}
                {templateQuestion.state === LifecycleState.ACTIVE && (
                  <Button variant="secondary" onClick={handleMarkAsDraft}>
                    Mark as Draft
                  </Button>
                )}
                {templateQuestion.state === LifecycleState.ARCHIVED ? (
                  <Button variant="secondary" onClick={handleMarkAsDraft}>
                    Restore from Archive <ArrowCounterClockwise />
                  </Button>
                ) : (
                  <Button variant="secondary" color="red" onClick={handleArchive}>
                    Archive <Trash size={16} />
                  </Button>
                )}
                <Button variant="secondary" onClick={handleUpdateExistingRoundQuestions}>
                  Update Existing Rounds {isSaving && <Spinner size="sm" />}
                </Button>
                <Button variant="secondary" onClick={handleAddToRound}>
                  Add to Round {currentRound?.name} {isSaving && <Spinner size="sm" />}
                </Button>
              </Flex>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Tabs>
            <TabList>
              <Tab>Preview</Tab>
              <Tab>Source Digest</Tab>
              <Tab>Comments</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <TQPreview
                  templateQuestion={templateQuestion}
                  questionParts={questionParts}
                  framework={framework}
                  refresh={refresh}
                />
              </TabPanel>
              <TabPanel>
                <TQSrcDigest templateQuestion={templateQuestion} questionParts={questionParts} />
              </TabPanel>
              <TabPanel>
                <TQComments templateQuestion={templateQuestion} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </GridItem>
      </Grid>
    </IssbContainer>
  );
}

// allow "only show part-a if q17 = true" conditions
const PART_LEVEL_CONDITIONS = true;

function QuestionPartItemEditor({ part, isColumn, handleDeletePart, refresh }) {
  if (part.type === QAPartType.table) {
    // init columns if needed
    part.columns = part.columns || [];
  }
  /** when type changes, strip down to the common properties (to clear out irrevelant and now hidden properties) */
  const cleanOut = (part) => {
    delete part.options;
    delete part.condition;
    delete part.metricType;
    delete part.unit;
    delete part.grouped;
    delete part.multiple;
    delete part.group;
    delete part.columns;
    delete part.rows;
    delete part.grouped;
  };
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <ListItem mb={2} onClick={() => setOpen(true)} style={{ border: "1px solid #ccc" }}>
        <Flex gap={2} direction="row" w="100%" justify="space-between">
          <Text>{part.questionPart}</Text>
          <Button variant="secondary" size="xs" onClick={() => setOpen(true)}>
            <CaretDown size={16} />
          </Button>
        </Flex>
        {part.type === QAPartType.table && part.rows && <Text>Rows: {toArray(part.rows).join(", ")}</Text>}
        {part.type === QAPartType.table && part.columns && (
          <>
            <Text>Columns:</Text>
            {part.columns.map((cp) => (
              <Text>{cp.questionPart}</Text>
            ))}
          </>
        )}
      </ListItem>
    );
  }
  return (
    <ListItem mb={2} style={{ border: "1px solid #ccc" }}>
      <Flex gap={2} direction="row" w="100%">
        <PropInput label={isColumn ? "Table Column" : "Question Part"} prop="questionPart" item={part} flex={1} />
        <Button variant="secondary" size="sm" onClick={handleDeletePart}>
          <Trash />
        </Button>
        <Button variant="secondary" size="xs" onClick={() => setOpen(false)}>
          <CaretUp size={16} />
        </Button>
      </Flex>
      <PropInput
        prop="type"
        item={part}
        type="select"
        options={isColumn ? Object.keys(QAPartType).filter((t) => t !== QAPartType.table) : Object.keys(QAPartType)}
        onChange={() => {
          cleanOut(part);
          refresh();
        }}
      />

      {part.type === "metric" && (
        <PropInput prop="metricType" item={part} type="select" options={[null, "money", "weight"]} onChange={refresh} />
      )}
      {part.type === "metric" && part.metricType && (
        <PropInput
          prop="unit"
          item={part}
          type="select"
          options={[""].concat(getMetricOptions(part.metricType).map((o) => o.value))}
          onChange={refresh}
        />
      )}

      {part.type === "select" && <QPartSelectEditor part={part} refresh={refresh} />}

      {PART_LEVEL_CONDITIONS && (
        <PropInput
          prop="condition"
          item={part}
          help="If set, only show this part if the condition is true. The condition uses the same syntax as for question-level conditions."
          onChange={refresh}
        />
      )}
      {(part.type === QAPartType.metric || part.type === QAPartType.percentage || part.type === QAPartType.number) && (
        <PropInput
          prop="autoCalculated"
          item={part}
          type="checkbox"
          label="Auto-calculated"
          help="If true, the answer will be auto-calculated by CDP from other answers"
        />
      )}
      {part.autoCalculated && (
        <PropInput prop="autoCalculatedFormula" item={part} type="text" label="Auto-calculated formula" />
      )}

      {part.type === QAPartType.table /* rows & a recursive editor for columns */ && (
        <>
          <SmallText>
            A `table` part creates a table of rows x columns -- add columns by adding column question parts below.
          </SmallText>
          <PropInput
            prop="rows"
            label="Rows, comma separated (leave blank for user-adds-rows)"
            item={part}
            onChange={refresh}
          />
          <Text>Columns</Text>
          <OrderedList>
            {part.columns?.map((columnPart, index) => (
              <QuestionPartItemEditor
                key={index}
                part={columnPart}
                isColumn={true}
                handleDeletePart={() => {
                  part.columns = part.columns.filter((_, i) => i !== index);
                  refresh();
                }}
                refresh={refresh}
              />
            ))}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                part.columns.push({});
                refresh();
              }}
            >
              Add Column
            </Button>
          </OrderedList>
        </>
      )}
    </ListItem>
  );
}

function QPartSelectEditor({ part, refresh }) {
  // is it a multi-group select?
  let isMultiGroup = part.grouped;
  if (!part.options) {
    part.options = [];
  }
  if (typeof part.options === "string") {
    console.log("QPartSelectEditor: part.options is a string?! convert it", part);
    part.options = part.options.split(",").map((o) => o.trim()); // HACK handle bad data, seen 1st Sept 2025
    if (isMultiGroup) {
      part.options = part.options.map((o) => ({ group: o, options: [] }));
    }
  }
  const options = part.options;

  const handleChangeGrouped = (e) => {
    console.log("handleChangeGrouped", e?.target?.value, part.grouped, isMultiGroup);
    // convert string[] options to {group: string, options: string[]}
    if (part.options) {
      if (part.grouped) {
        part.options = part.options.map((o) => ({ group: o, options: [] }));
      } else {
        part.options = part.options.map((o) => o.group);
      }
    }
    refresh();
  };

  return (
    <>
      {!isMultiGroup && (
        <PropInput
          prop="options"
          label="Options, comma separated"
          item={part}
          onChange={refresh}
          help={`Special options: You can use ${OPTIONS_COUNTRIES_MARKER} or ${OPTIONS_RIVERS_MARKER} to add a list of countries or rivers`}
        />
      )}
      {isMultiGroup &&
        options.map((optionGroup, index) => (
          <Fragment key={optionGroup.group}>
            <Flex gap={2} direction="row" w="100%" align="flex-end">
              <PropInput prop="group" label="Group name" item={optionGroup} onChange={refresh} flex={1} />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  part.options = part.options.filter((_, i) => i !== index);
                  refresh();
                }}
              >
                <small>Remove this option group</small>
              </Button>
            </Flex>
            <PropInput
              prop="options"
              label={"Options for group " + (optionGroup.group || index + 1) + ", comma separated"}
              item={optionGroup}
              onChange={refresh}
            />
          </Fragment>
        ))}
      {isMultiGroup && (
        <Button
          mt={2}
          variant="secondary"
          size="sm"
          onClick={() => {
            part.options.push({});
            refresh();
          }}
        >
          <small>Add option group</small>
        </Button>
      )}
      <PropInput prop="multiple" type="checkbox" label="Can select multiple" item={part} onChange={refresh} />
      <PropInput
        prop="grouped"
        type="checkbox"
        label="Options are grouped"
        item={part}
        onChange={handleChangeGrouped}
        help="If true, the options are presented in groups"
      />
    </>
  );
}

function TQPreview({ templateQuestion, questionParts, framework, refresh }) {
  const [answer, setAnswer] = useState(null);
  const [qData, setQData] = useState({ ...templateQuestion, parts: questionParts });
  useEffect(() => {
    const newQData = { ...templateQuestion, parts: questionParts };
    setQData(newQData);
    console.log("TQPreview setQData", newQData);
    setAnswer(null);
  }, [templateQuestion, questionParts]);
  const canUseHtml = framework?.answersCanUseHtml !== false;
  assert(!answer || typeof answer === "string", "TQPreview answer must be a string", answer);
  return (
    <Card overflowX="auto" style={{ maxWidth: "40vw" /* prevent tables going off-screen without a scrollbar*/ }}>
      <CardBody>
        <H3>Preview</H3>
        <Box p={4} borderWidth={1} borderRadius="md" overflowX="auto">
          <QuestionHeader questionData={templateQuestion} />

          <QuestionPartsList questionData={qData} answer={answer} setAnswer={setAnswer} />

          <QuestionSpec questionData={templateQuestion} />

          <Box mt={4} p={2} bg="gray.50" borderRadius="md">
            <Text fontSize="sm" color="gray.600">
              {templateQuestion.regulation &&
                templateQuestion.issbReference &&
                `${templateQuestion.regulation}: ${templateQuestion.issbReference}`}
            </Text>
          </Box>
        </Box>
      </CardBody>
    </Card>
  );
}

function TQSrcDigest({ templateQuestion }) {
  return (
    <Card>
      <CardBody>
        <H3>
          Source Digest: Pages: {templateQuestion?.srcPageStart} - {templateQuestion?.srcPageEnd}
        </H3>
        <MarkdownText>{templateQuestion?.srcDigest}</MarkdownText>
      </CardBody>
    </Card>
  );
}

function TQComments({ templateQuestion }) {
  return (
    <Card>
      <CardBody>
        <CommentSection targetId={templateQuestion.id} targetTable="issb_template_question" />
      </CardBody>
    </Card>
  );
}

export default IssbTemplateQuestionEditorPage;
